import React, { ReactNode, useEffect, useState } from "react";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from "@mui/icons-material/Delete";
import { RetryImage } from "../../../components/RetryImage";

import DefaultTable from "../../../components/Table/DefaultTable";
import { deleteModel, getSearchModels } from "../../../apis/model";
import { CustomDialog } from "../../../components/modal/CustomDialog";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { useOptionData } from "../../../hook/useOptionData";
import { filteredOptions } from "../../../utils/option";
import { Loader } from "../../../components/Loader";
import { RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { setModelState } from "../../../store/actions";

interface TRowData<T> {
	[key: string]: T;
}

const headerData: string[] = ["Model", "Options", "MSRP", `${process.env.REACT_APP_DEALER_NAME} Price`, ""];
const headerColumnWidth: number[] = [20, 45, 10, 15, 10];

const sortableFields: any = {
	"Product Name": "model_name",
	[`${process.env.REACT_APP_DEALER_NAME} Price`]: "dealer_price",
	MSRP: "MSRP",
};

export const Models: React.FC = () => {
	const history = useNavigate();

	const [models, setModels] = useState<TRowData<ReactNode>[]>([]);
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const [modelId, setModelId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean | false>(true);
	const [count, setCount] = useState<number>(0);

	const { options, loading: optionLoading } = useOptionData();
	const { showSnackBar } = useGlobalSnackBar();
	const dispatch = useDispatch();
	const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

	const modelState = useSelector(
		(state: RootState) => state.adminAuth.modelState
	);

	const OptionsCell = ({ options }: { options: string[] }) => {
		const [showAll, setShowAll] = useState(false);
		const displayedOptions = showAll ? options : options.slice(0, 5);

		return (
			<div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
				{displayedOptions.map((eachOption, idx) => {
					const isTruncated = eachOption.length > 30;
					const truncatedLabel = isTruncated ? `${eachOption.slice(0, 30)}...` : eachOption;

					const chip = (
						<Chip
							key={idx}
							label={truncatedLabel}
							sx={{
								backgroundColor: "#e0e0e0",
								color: "#000",
								fontSize: "0.75rem",
								fontWeight: 500,
								borderRadius: "8px",
								height: "auto",
								'&:hover': {
									backgroundColor: "#d0d0d0",
								},
								padding: "5px 0",
								background: "white"
							}}
						/>
					);

					return isTruncated ? (
						<Tooltip key={idx} title={eachOption}>
							<span>{chip}</span>
						</Tooltip>
					) : chip;
				})}
				{options.length > 5 && (
					<Chip
						label={showAll ? "Hide" : "More"}
						onClick={() => setShowAll((prev) => !prev)}
						style={{ cursor: "pointer" }}
						sx={{
							height: "auto",
							padding: "5px 0",
							borderRadius: "8px",
						}}
					/>
				)}
			</div>
		);
	};

	const fetchModels = async () => {
		try {
			const { data: modelData, counts } = await getSearchModels({
				page: modelState.page + 1,
				pageSize: modelState.pageSize,
				orderBy: modelState.orderBy && sortableFields[modelState.orderBy],
				order: modelState.order,
			});

			const transformedData = modelData.map((model: any) => {
				const optionList = filteredOptions(
					options,
					JSON.parse(model.applicable_option_ids)
				);

				const imageUrl = typeof JSON.parse(model.images)?.[0] === "string"
					? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${JSON.parse(model.images)?.[0]}`
					: undefined;

				return {
					model: (
						<Box
							component={Link}
							to={`/models/${model.ID}`}
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 0.5,
								textDecoration: "none",
								color: "inherit",
								py: 0.5,
							}}
						>
							<RetryImage
								sx={{ width: 90, height: 90, borderRadius: 1 }}
								variant="square"
								src={imageUrl}
							/>
							<Typography variant="body2" fontWeight={500} sx={{ color: "primary.main", textAlign: "center" }}>
								{model.model_name}
							</Typography>
						</Box>
					),
					options: <OptionsCell options={optionList} />,
					msrp: `$${model.MSRP?.toFixed(2)}`,
					dealer_price: `$${model.dealer_price?.toFixed(2)}`,
					edit: (
						<div>
							<Tooltip title="Edit">
								<IconButton size="small" color="primary" onClick={() => history(`/models/${model.ID}`)}>
									<ModeEditIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Delete">
							<IconButton
								size="small"
								color="error"
								onClick={() => {
									if (currentUser?.permission === "Administrator") {
										setModelId(model.ID);
										setOpenDeleteModal(true);
									} else {
										permissionErrorMessage();
									}
								}}
							>
								<DeleteIcon />
							</IconButton>
							</Tooltip>
						</div>
					),
				}
			});

			setModels(transformedData);
			setCount(counts);
			setIsLoading(false);
		} catch (error) {
			console.error("Failed to fetch admins", error);
		}
	};

	useEffect(() => {
		setIsLoading(true);
		if (!optionLoading) {
			fetchModels();
		}
	}, [optionLoading, modelState]);

	// Function to handle sorting
	const handleSort = (column: string) => {
		if (!sortableFields[column]) return;
		const isAsc = modelState.orderBy === column && modelState.order === "asc";
		dispatch(setModelState({ order: isAsc ? "desc" : "asc", orderBy: column }));
	};


	const onDelete = async () => {
		try {
			await deleteModel({ ID: modelId || "" });
			showSnackBar("Delete this model");
			fetchModels();
			setOpenDeleteModal(false);
		} catch (err) {
			showSnackBar("Error", "error");
		}
	};

	const handleChangePage = (page: number) => {
		dispatch(setModelState({ page }));
	};

	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(setModelState({ page: 0, pageSize: Number(e.target.value) }));
	};

	const permissionErrorMessage = () => {
		showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
	};

	if (optionLoading || isLoading) return <Loader isLoading={isLoading} />;

	return (
		<>
			<Box sx={{ flexGrow: 1 }}>
				<Grid
					container
					spacing={2}
					justifyContent="space-between"
					pl={{ md: 0, xs: 4 }}
					pr={{ md: 0, xs: 4 }}
				>
					<Grid item sm={2} xs={12}>
						<Card style={{ textAlign: "center" }}>
							<CardContent sx={{ pb: "10px !important" }}>
								<Typography variant="h5" component="div">
									{count}
								</Typography>
								<Typography sx={{ mb: 1.5 }} color="text.secondary">
									Base Models
								</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item sm={2} xs={12}>
						<Card
							style={{ textAlign: "center", cursor: "pointer" }}
							onClick={() => {
								if (currentUser?.permission !== "Administrator") {
									permissionErrorMessage();
									return;
								}
								history("/models/create");
							}}
							sx={{ textAlign: "center" }}
						>
							<CardContent sx={{ pb: "10px !important" }}>
								<Typography variant="h5" component="div">
									+
								</Typography>
								<Typography sx={{ mb: 1.5 }} color="text.secondary">
									Add Model
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>

			<Box sx={{ flexGrow: 1, mt: 2 }}>
				<DefaultTable
					headerData={headerData}
					headerColumnWidth={headerColumnWidth}
					rowsData={models}
					rowsPerPage={modelState.pageSize}
					page={modelState.page}
					count={count}
					handleChangePage={(
						e: React.ChangeEvent<HTMLInputElement>,
						page: number
					) => handleChangePage(page)}
					handleChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChangeRowsPerPage(e)
					}
					handleSort={handleSort}
					order={modelState.order}
					orderBy={modelState.orderBy}
					sortableFields={sortableFields}
				/>
			</Box>

			<CustomDialog
				title="Are you sure you want to delete this model?"
				openModal={openDeleteModal}
				handleClose={() => setOpenDeleteModal(false)}
				handleAction={onDelete}
				buttonTitle={"Yes"}
				children={undefined}
			/>
		</>
	);
};
